import React, { useState, useEffect } from 'react';
import { Container, Row, Col, Card, Button, Spinner, Alert, Badge, Form } from 'react-bootstrap';
import { useParams, useNavigate, Link } from 'react-router-dom';
import { sickLeaveApplicationsApiService } from '../../services/apiServices';
import { SickLeaveApplicationResponse, SickLeaveApplicationStatus } from '../../api-client';
import { getSickLeaveApplicationStatusLabel } from '../utils/statusConverters';

const SickLeaveApplicationsDetailPage: React.FC = () => {
  const { applicationId } = useParams<{ applicationId: string }>();
  const navigate = useNavigate();

  const [application, setApplication] = useState<SickLeaveApplicationResponse | null>(null);
  const [loading, setLoading] = useState<boolean>(true);
  const [error, setError] = useState<string | null>(null);
  const [updating, setUpdating] = useState<boolean>(false);
  const [updateError, setUpdateError] = useState<string | null>(null);
  const [showExtendForm, setShowExtendForm] = useState(false);
  const [newEndDate, setNewEndDate] = useState('');
  const [extending, setExtending] = useState(false);
  const [extendError, setExtendError] = useState<string | null>(null);
  const [extendSuccess, setExtendSuccess] = useState(false);

  useEffect(() => {
    if (!applicationId) {
      navigate('/sick-leave/applications');
      return;
    }

    const loadApplication = async () => {
      try {
        setLoading(true);
        const response = await sickLeaveApplicationsApiService.getApplication(applicationId);
        setApplication(response.data || null);
      } catch (err: any) {
        console.error('Ошибка при загрузке заявки:', err);
        setError(err.message || 'Ошибка при загрузке заявки');
      } finally {
        setLoading(false);
      }
    };

    loadApplication();
  }, [applicationId, navigate]);

  const handleStatusUpdate = async (newStatus: SickLeaveApplicationStatus) => {
    if (!applicationId) return;

    try {
      setUpdating(true);
      setUpdateError(null);
      await sickLeaveApplicationsApiService.updateStatus(applicationId, newStatus);

      // Обновляем локальное состояние
      setApplication((prev: SickLeaveApplicationResponse | null) =>
        prev ? { ...prev, status: newStatus } : null
      );
    } catch (err: any) {
      console.error('Ошибка при обновлении статуса:', err);
      setUpdateError(err.message || 'Ошибка при обновлении статуса');
    } finally {
      setUpdating(false);
    }
  };

  const handleExtend = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!applicationId || !newEndDate) return;

    try {
      setExtending(true);
      setExtendError(null);
      const response = await sickLeaveApplicationsApiService.adminExtendApplication(
        applicationId,
        newEndDate
      );
      setApplication(response.data || null);
      setShowExtendForm(false);
      setExtendSuccess(true);
      setNewEndDate('');
      setTimeout(() => setExtendSuccess(false), 3000);
    } catch (err: any) {
      setExtendError(err.message || 'Ошибка при продлении больничного');
    } finally {
      setExtending(false);
    }
  };

  const getStatusBadgeVariant = (status: SickLeaveApplicationStatus) => {
    switch (status) {
      case SickLeaveApplicationStatus.PENDING:
        return 'warning';
      case SickLeaveApplicationStatus.ACTIVE:
        return 'info';
      case SickLeaveApplicationStatus.COMPLETED:
        return 'success';
      case SickLeaveApplicationStatus.CANCELED:
        return 'danger';
      default:
        return 'secondary';
    }
  };

  if (loading) {
    return (
      <Container
        fluid
        className="d-flex justify-content-center align-items-center"
        style={{ minHeight: '50vh' }}
      >
        <div className="text-center">
          <Spinner animation="border" />
          <p className="mt-2">Загрузка заявки...</p>
        </div>
      </Container>
    );
  }

  if (error || !application) {
    return (
      <Container fluid>
        <Row className="mt-4">
          <Col>
            <Alert variant="danger">{error || 'Заявка не найдена'}</Alert>
            <Button variant="primary" onClick={() => navigate('/sick-leave/applications')}>
              Вернуться к списку
            </Button>
          </Col>
        </Row>
      </Container>
    );
  }

  return (
    <Container fluid>
      <Row className="mt-4">
        <Col>
          <div className="d-flex justify-content-between align-items-center mb-4">
            <h2>Заявка №{application.id}</h2>
            <Link to="/sick-leave/applications">
              <Button variant="outline-secondary">Вернуться к списку</Button>
            </Link>
          </div>

          {updateError && <Alert variant="danger">{updateError}</Alert>}
          {extendSuccess && <Alert variant="success">Больничный успешно продлен!</Alert>}
          {extendError && <Alert variant="warning">{extendError}</Alert>}

          <Card>
            <Card.Header>
              <Badge bg={getStatusBadgeVariant(application.status)}>
                {getSickLeaveApplicationStatusLabel(application.status)}
              </Badge>
            </Card.Header>
            <Card.Body>
              <Row>
                <Col md={6}>
                  <h5>Основная информация</h5>
                  <table className="table table-bordered">
                    <tbody>
                      <tr>
                        <th style={{ width: '40%' }}>ID заявки</th>
                        <td>{application.id}</td>
                      </tr>
                      <tr>
                        <th>Пользователь</th>
                        <td>
                          {application.user?.profile?.full_name && (
                            <div>
                              <strong>{application.user.profile.full_name}</strong>
                            </div>
                          )}
                          <div>
                            <strong>{application.user?.username || '—'}</strong>
                          </div>
                          <small className="text-muted">{application.user?.email || '—'}</small>
                        </td>
                      </tr>
                      <tr>
                        <th>Дата начала</th>
                        <td>{new Date(application.start_date).toLocaleDateString()}</td>
                      </tr>
                      <tr>
                        <th>Дата окончания</th>
                        <td>{new Date(application.end_date).toLocaleDateString()}</td>
                      </tr>
                      <tr>
                        <th>Статус</th>
                        <td>
                          <Badge bg={getStatusBadgeVariant(application.status)}>
                            {getSickLeaveApplicationStatusLabel(application.status)}
                          </Badge>
                        </td>
                      </tr>
                      <tr>
                        <th>Дата создания</th>
                        <td>{new Date(application.created_at).toLocaleString()}</td>
                      </tr>
                      <tr>
                        <th>Можно продлить</th>
                        <td>{application.is_extensible ? 'Да' : 'Нет'}</td>
                      </tr>
                    </tbody>
                  </table>
                </Col>

                <Col md={6}>
                  <hr />

                  <h5>Обновление статуса</h5>
                  <Form>
                    <Form.Group className="mb-3">
                      <Form.Label>Установить статус:</Form.Label>
                      <div className="d-grid gap-2">
                        <Button
                          variant={
                            application.status === SickLeaveApplicationStatus.PENDING
                              ? 'warning'
                              : 'outline-warning'
                          }
                          onClick={() => handleStatusUpdate(SickLeaveApplicationStatus.PENDING)}
                          disabled={
                            updating || application.status === SickLeaveApplicationStatus.PENDING
                          }
                          size="sm"
                        >
                          {application.status === SickLeaveApplicationStatus.PENDING ? '✓ ' : ''}
                          Ожидает обработки
                        </Button>
                        <Button
                          variant={
                            application.status === SickLeaveApplicationStatus.ACTIVE
                              ? 'info'
                              : 'outline-info'
                          }
                          onClick={() => handleStatusUpdate(SickLeaveApplicationStatus.ACTIVE)}
                          disabled={
                            updating || application.status === SickLeaveApplicationStatus.ACTIVE
                          }
                          size="sm"
                        >
                          {application.status === SickLeaveApplicationStatus.ACTIVE ? '✓ ' : ''}
                          Активен
                        </Button>
                        <Button
                          variant={
                            application.status === SickLeaveApplicationStatus.COMPLETED
                              ? 'success'
                              : 'outline-success'
                          }
                          onClick={() => handleStatusUpdate(SickLeaveApplicationStatus.COMPLETED)}
                          disabled={
                            updating || application.status === SickLeaveApplicationStatus.COMPLETED
                          }
                          size="sm"
                        >
                          {application.status === SickLeaveApplicationStatus.COMPLETED ? '✓ ' : ''}
                          Завершен
                        </Button>
                        <Button
                          variant={
                            application.status === SickLeaveApplicationStatus.CANCELED
                              ? 'danger'
                              : 'outline-danger'
                          }
                          onClick={() => handleStatusUpdate(SickLeaveApplicationStatus.CANCELED)}
                          disabled={
                            updating || application.status === SickLeaveApplicationStatus.CANCELED
                          }
                          size="sm"
                        >
                          {application.status === SickLeaveApplicationStatus.CANCELED ? '✓ ' : ''}
                          Отменен
                        </Button>
                      </div>
                    </Form.Group>
                  </Form>
                </Col>
              </Row>

              {application.is_extensible && (
                <Row className="mt-4">
                  <Col>
                    <hr />
                    <h5>Продление больничного</h5>
                    {!showExtendForm ? (
                      <Button
                        variant="outline-primary"
                        onClick={() => {
                          setShowExtendForm(true);
                          setExtendError(null);
                        }}
                      >
                        Продлить больничный
                      </Button>
                    ) : (
                      <Card>
                        <Card.Body>
                          <Form onSubmit={handleExtend}>
                            {extendError && <Alert variant="danger">{extendError}</Alert>}
                            <Form.Group className="mb-3">
                              <Form.Label>Новая дата окончания</Form.Label>
                              <Form.Control
                                type="date"
                                value={newEndDate}
                                onChange={(e) => setNewEndDate(e.target.value)}
                                min={
                                  new Date(
                                    Math.max(Date.now(), new Date(application.end_date).getTime())
                                  )
                                    .toISOString()
                                    .split('T')[0]
                                }
                                disabled={extending}
                                style={{ width: 'auto' }}
                                required
                              />
                              <Form.Text className="text-muted">
                                Дата должна быть позже текущей даты окончания (
                                {new Date(application.end_date).toLocaleDateString()})
                              </Form.Text>
                            </Form.Group>
                            <div className="d-flex gap-2">
                              <Button
                                variant="primary"
                                type="submit"
                                disabled={extending || !newEndDate}
                              >
                                {extending ? (
                                  <>
                                    <Spinner animation="border" size="sm" className="me-1" />
                                    Продление...
                                  </>
                                ) : (
                                  'Продлить'
                                )}
                              </Button>
                              <Button
                                variant="outline-secondary"
                                onClick={() => {
                                  setShowExtendForm(false);
                                  setExtendError(null);
                                  setNewEndDate('');
                                }}
                                disabled={extending}
                              >
                                Отмена
                              </Button>
                            </div>
                          </Form>
                        </Card.Body>
                      </Card>
                    )}
                  </Col>
                </Row>
              )}
            </Card.Body>
          </Card>
        </Col>
      </Row>
    </Container>
  );
};

export default SickLeaveApplicationsDetailPage;
